import { Container, Graphics } from 'pixi.js';
import type { FederatedPointerEvent } from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { NodeLayout } from './layout';
import type { NodeSprite } from './renderer';
import type { Theme } from './theme';
import type { Status } from './data';
import {
  ACTION_BUTTON_RADIUS,
  STATUS_BTN_ACTIVE_ALPHA,
  STATUS_BTN_INACTIVE_ALPHA,
  STATUS_CYCLE,
  createNodeSprite,
  drawLines,
  refreshNodeStatus,
  refreshNodeDescription,
} from './renderer';
import { FIREWORK_ICONS, ICONS, drawIcon } from './icons';

const STATUS_HOVER_ALPHA      = 0.75;
const STATUS_HOVER_SCALE      = 1.15;
const STATUS_TWEEN_DURATION_MS = 150;
import { makeNodeLayout, relayoutDescendants } from './layout';
import { openTextPrompt, openDescriptionModal } from './modal';
import * as api from './api';

export interface InteractionsHandle {
  /** Mounts a brand-new sprite at runtime: creates it, wires events, adds to viewport. */
  registerNode: (layout: NodeLayout) => NodeSprite;
  /** Animate viewport to (x, y) and optionally a target zoom level. */
  focusOn:      (x: number, y: number, targetScale?: number) => void;
  /** Briefly pulse a ring around the node to draw attention. */
  pulseNode:    (id: string) => void;
  /** Reveal the node's action + status bars (same as a click on the circle). */
  selectNode:   (id: string) => void;
  /**
   * Event pulse: zoom/pan to the node, then (once the zoom lands) a high-contrast
   * ring plus the optional onArrive callback — but WITHOUT selecting it or
   * navigating the event panel, so a live event brings its node into view while
   * keeping the panel's scope. Returns false if the node isn't on this page.
   */
  pulseEvent:   (id: string, onArrive?: () => void) => boolean;
  /**
   * Replay an event's animation on its node: zoom/pan to it (targets the node),
   * high-contrast pulse, and the status flourish (firework / shake / bounce) —
   * WITHOUT changing the node's state. Returns false if the node isn't on this
   * page. Used when an event card is clicked.
   */
  playEventAnimation: (id: string, status: Status | null) => boolean;
  /** Id of the currently-selected node, or null if none is selected. */
  getSelectedId: () => string | null;
  /**
   * Reflect a status change on the node visually only — updates the layout +
   * sprite, no API call. Used after an event already persisted the new status.
   */
  applyStatusVisual: (id: string, status: Status) => void;
  /**
   * Reflect a rename on the node visually only — updates the layout + sprite
   * text, no API call. Used after an event already persisted the new label.
   */
  applyLabelVisual: (id: string, label: string) => void;
  /** Redraw the edge graphics, applying the current selection-path highlight. */
  redrawLines:  () => void;
}

const PULSE_DURATION_MS = 900;
const PULSE_REPEATS     = 2;
const FOCUS_ZOOM        = 1.6;
// High-contrast ring color for event-driven pulses — deliberately different
// from the usual theme pulse (yellow in night). Hot magenta reads clearly on
// both the dark and light backgrounds and isn't a status/action color.
const EVENT_PULSE_COLOR = 0xff2d95;

// ─── État du drag ─────────────────────────────────────────────────────────────

interface DragState {
  sprite:  NodeSprite;
  offsetX: number;
  offsetY: number;
  startX:  number;
  startY:  number;
}

// ─── Easing ───────────────────────────────────────────────────────────────────

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// ─── Animation de pan sur le ticker ──────────────────────────────────────────

const PAN_DURATION_MS = 900;

function createPanController(viewport: Viewport, requestRender: () => void) {
  let rafId: number | null = null;

  const stop = (): void => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  // onDone fires once the pan lands (t reaches 1). It is NOT called if the pan is
  // interrupted by another focusTo/stop, so a superseded animation never triggers it.
  const focusTo = (
    targetX: number,
    targetY: number,
    targetScale?: number,
    onDone?: () => void,
  ): void => {
    stop();
    const startX     = viewport.center.x;
    const startY     = viewport.center.y;
    const startScale = viewport.scale.x;
    const endScale   = targetScale ?? startScale;
    const startTime  = performance.now();

    const tick = (now: number): void => {
      const t  = Math.min(1, (now - startTime) / PAN_DURATION_MS);
      const et = easeInOutCubic(t);
      const s  = startScale + (endScale - startScale) * et;
      viewport.scale.set(s);
      viewport.moveCenter(
        startX + (targetX - startX) * et,
        startY + (targetY - startY) * et,
      );
      requestRender();
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
        onDone?.();
      }
    };

    rafId = requestAnimationFrame(tick);
  };

  const panTo = (targetX: number, targetY: number): void => focusTo(targetX, targetY);

  return { panTo, focusTo, stop };
}

// ─── Setup ────────────────────────────────────────────────────────────────────

export function setupInteractions(
  viewport:           Viewport,
  sprites:            Map<string, NodeSprite>,
  layouts:            Map<string, NodeLayout>,
  linesGraphics:      Graphics,
  getTheme:           () => Theme,
  requestRender:      () => void,
  pageId:             string,
  onLayoutsChanged?:  () => void,
  onSelectionChange?: (layout: NodeLayout | null) => void,
): InteractionsHandle {
  let drag: DragState | null = null;
  let selected: NodeSprite | null = null;

  // Edges (parentId|childId) belonging to the path root → … → selected.
  // drawLines uses this to color those edges with theme.pulseColor.
  let selectedPathEdges = new Set<string>();

  const updateSelectedPathEdges = (sprite: NodeSprite | null): void => {
    selectedPathEdges = new Set();
    if (!sprite) return;
    let cursor: NodeLayout | undefined = sprite.layout;
    while (cursor?.parentId) {
      selectedPathEdges.add(`${cursor.parentId}|${cursor.id}`);
      cursor = layouts.get(cursor.parentId);
    }
  };

  const redrawLines = (): void => {
    drawLines(linesGraphics, layouts, getTheme(), selectedPathEdges);
  };

  // Drag-time coalescer: multiple pointermoves in one frame collapse to one
  // line rebuild. Every other call site (theme toggle, rotate, addSubtask,
  // delete, drag-end) keeps using `redrawLines` directly — those are rare
  // enough that synchronous redraws are clearer.
  let linesDirty = false;
  const scheduleLinesRedraw = (): void => {
    if (linesDirty) return;
    linesDirty = true;
    requestAnimationFrame(() => {
      linesDirty = false;
      redrawLines();
      requestRender();
    });
  };

  // Manual double-click tracking. Two consecutive clicks on the same sprite
  // within DOUBLE_CLICK_MS open the add-subtask modal.
  const DOUBLE_CLICK_MS = 350;
  let lastClickAt = 0;
  let lastClicked: NodeSprite | null = null;

  const pan = createPanController(viewport, requestRender);

  const setBarsVisible = (sprite: NodeSprite, visible: boolean): void => {
    sprite.actionBar.container.visible = visible;
    sprite.statusBar.container.visible = visible;
  };

  const clearSelection = (): void => {
    if (!selected) return;
    setBarsVisible(selected, false);
    selected = null;
    updateSelectedPathEdges(null);
    redrawLines();
    cancelPathPulse();
    requestRender();
    onSelectionChange?.(null);
  };

  const select = (sprite: NodeSprite): void => {
    if (selected === sprite) return;
    if (selected) setBarsVisible(selected, false);
    selected = sprite;
    setBarsVisible(sprite, true);
    updateSelectedPathEdges(sprite);
    redrawLines();
    requestRender();
    linePulsePaths(buildSelectionPaths(sprite));
    onSelectionChange?.(sprite.layout);
  };

  const deleteNode = (id: string): void => {
    const toDelete = new Set<string>();
    const stack = [id];
    while (stack.length > 0) {
      const curr = stack.pop() as string;
      if (toDelete.has(curr)) continue;
      toDelete.add(curr);
      const lay = layouts.get(curr);
      if (lay) stack.push(...lay.childIds);
    }

    const rootLayout = layouts.get(id);
    if (rootLayout?.parentId) {
      const parent = layouts.get(rootLayout.parentId);
      if (parent) parent.childIds = parent.childIds.filter(c => c !== id);
    }

    for (const delId of toDelete) {
      const sprite = sprites.get(delId);
      if (sprite) {
        // Animate then destroy. Drop from sprites map immediately so the rest
        // of the system stops considering it a live node.
        crumple(sprite.container);
        sprites.delete(delId);
      }
      layouts.delete(delId);
    }

    selected = null;
    cancelPathPulse();
    redrawLines();
    requestRender();
    api.deleteNode(id);
    onLayoutsChanged?.();
  };

  const addSubtask = async (parent: NodeSprite): Promise<void> => {
    const label = await openTextPrompt(`New subtask of "${parent.layout.label}"`);
    if (!label) return;

    let apiNode;
    try {
      apiNode = await api.createNode(pageId, label, parent.layout.id);
    } catch (err) {
      console.warn('[api] createNode failed', err);
      return;
    }

    const child = makeNodeLayout({
      id:       apiNode.id,
      label:    apiNode.label,
      level:    parent.layout.level + 1,
      status:   apiNode.status,
      parentId: parent.layout.id,
      childIds: [],
      x:        parent.layout.x,
      y:        parent.layout.y,
    });
    layouts.set(child.id, child);
    parent.layout.childIds.push(child.id);

    // Fan-out the parent's subtree so siblings + new child get clean positions.
    relayoutDescendants(parent.layout.id, layouts);
    registerNode(child);

    const updates: { id: string; x: number; y: number }[] = [];
    for (const [id, s] of sprites) {
      const lay = layouts.get(id);
      if (!lay) continue;
      s.container.x = lay.x;
      s.container.y = lay.y;
      updates.push({ id, x: lay.x, y: lay.y });
    }
    redrawLines();
    requestRender();
    api.bulkPositions(updates);
    onLayoutsChanged?.();
    linePulse(parent.layout, child);
  };

  function wireSprite(sprite: NodeSprite): void {
    const { container, actionBar, statusBar } = sprite;

    container.eventMode = 'static';
    container.cursor    = 'pointer';

    container.on('pointerdown', (e: FederatedPointerEvent) => {
      const world = viewport.toWorld(e.global.x, e.global.y);
      drag = {
        sprite,
        offsetX: container.x - world.x,
        offsetY: container.y - world.y,
        startX:  e.global.x,
        startY:  e.global.y,
      };
      container.zIndex = 999;
      e.stopPropagation();
    });

    statusBar.buttons.forEach((btn, i) => {
      btn.container.on('pointerover', () => {
        const isActive = sprite.layout.status === STATUS_CYCLE[i];
        const toAlpha  = isActive ? STATUS_BTN_ACTIVE_ALPHA : STATUS_HOVER_ALPHA;
        tweenButton(btn.container, STATUS_HOVER_SCALE, toAlpha);
      });

      btn.container.on('pointerout', () => {
        const isActive = sprite.layout.status === STATUS_CYCLE[i];
        const toAlpha  = isActive ? STATUS_BTN_ACTIVE_ALPHA : STATUS_BTN_INACTIVE_ALPHA;
        tweenButton(btn.container, 1, toAlpha);
      });

      btn.container.on('pointerdown', (e: FederatedPointerEvent) => {
        e.stopPropagation();
        const target = STATUS_CYCLE[i];
        if (!target || sprite.layout.status === target) {
          // Re-clicking the active status still gives feedback.
          pulseRing(btn.container, ACTION_BUTTON_RADIUS, 1);
          return;
        }
        // Cancel any in-flight hover tweens so the snap from refreshNodeStatus sticks.
        statusBar.buttons.forEach(b => cancelTween(b.container));
        sprite.layout.status = target;
        refreshNodeStatus(sprite, getTheme());
        bounce(sprite.statusBadge.container);
        if (target === 'blocked') shake(sprite);
        if (target === 'done') triggerFirework(sprite);
        pulseRing(btn.container, ACTION_BUTTON_RADIUS, 1);
        api.patchStatus(sprite.layout.id, target);
        requestRender();
        onLayoutsChanged?.();
      });
    });

    actionBar.addButton.container.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      addSubtask(sprite);
    });

    actionBar.rotateButton.container.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      relayoutDescendants(sprite.layout.id, layouts);
      const updates: { id: string; x: number; y: number }[] = [];
      for (const [id, s] of sprites) {
        const lay = layouts.get(id);
        if (!lay) continue;
        s.container.x = lay.x;
        s.container.y = lay.y;
        updates.push({ id, x: lay.x, y: lay.y });
      }
      redrawLines();
      requestRender();
      api.bulkPositions(updates);
    });

    actionBar.deleteButton.container.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      deleteNode(sprite.layout.id);
    });

    sprite.descBadge.container.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      void editDescription(sprite);
    });
  }

  const editDescription = async (sprite: NodeSprite): Promise<void> => {
    const next = await openDescriptionModal(
      `Description — "${sprite.layout.label}"`,
      sprite.layout.description,
    );
    if (next === null || next === sprite.layout.description) return;

    sprite.layout.description = next;
    refreshNodeDescription(sprite, getTheme());
    requestRender();
    api.patchDescription(sprite.layout.id, next);
    onLayoutsChanged?.();
  };

  function registerNode(layout: NodeLayout): NodeSprite {
    const sprite = createNodeSprite(layout, getTheme());
    sprite.container.zIndex = Math.max(1, 4 - layout.level);
    sprites.set(layout.id, sprite);
    viewport.addChild(sprite.container);
    wireSprite(sprite);
    dropIn(sprite.container);
    return sprite;
  }

  viewport.on('drag-start',  () => { pan.stop(); });
  viewport.on('wheel',       pan.stop);
  viewport.on('pinch-start', pan.stop);

  // Tap on empty canvas → clear selection. Sprite pointerdowns stopPropagation,
  // so this only fires for empty space. Distinguish tap from pan-drag via distance.
  let emptyTapStart: { x: number; y: number } | null = null;
  viewport.on('pointerdown', (e: FederatedPointerEvent) => {
    emptyTapStart = { x: e.global.x, y: e.global.y };
  });
  viewport.on('pointerup', (e: FederatedPointerEvent) => {
    if (!emptyTapStart) return;
    const dx = e.global.x - emptyTapStart.x;
    const dy = e.global.y - emptyTapStart.y;
    emptyTapStart = null;
    if (Math.hypot(dx, dy) < 5) {
      pan.stop();
      clearSelection();
    }
  });

  for (const sprite of sprites.values()) wireSprite(sprite);

  viewport.on('pointermove', (e: FederatedPointerEvent) => {
    if (!drag) return;
    const world = viewport.toWorld(e.global.x, e.global.y);
    drag.sprite.container.x = world.x + drag.offsetX;
    drag.sprite.container.y = world.y + drag.offsetY;
    drag.sprite.layout.x    = drag.sprite.container.x;
    drag.sprite.layout.y    = drag.sprite.container.y;
    // High-refresh-rate pointers can fire >120 Hz; coalesce line rebuilds
    // to one per frame regardless of how many move events arrive.
    scheduleLinesRedraw();

    // Drop a fading ghost every ~50 ms so motion leaves a trail.
    const now = performance.now();
    if (now - lastTrailAt > 50) {
      lastTrailAt = now;
      spawnTrailGhost(drag.sprite);
    }

    requestRender();
  });

  const endDrag = (e: FederatedPointerEvent): void => {
    if (!drag) return;
    const { sprite, startX, startY } = drag;
    const dx     = e.global.x - startX;
    const dy     = e.global.y - startY;
    const moved  = Math.hypot(dx, dy) >= 5;
    if (!moved) {
      const now      = performance.now();
      const isDouble = lastClicked === sprite && (now - lastClickAt) < DOUBLE_CLICK_MS;
      if (isDouble) {
        lastClickAt  = 0;
        lastClicked  = null;
        pan.stop();
        addSubtask(sprite);
      } else {
        lastClickAt  = now;
        lastClicked  = sprite;
        select(sprite);
        // Zoom in to at least FOCUS_ZOOM (don't de-zoom if user is already deeper).
        const targetZoom = Math.max(viewport.scale.x, FOCUS_ZOOM);
        pan.focusTo(sprite.layout.x, sprite.layout.y, targetZoom);
        pulseNode(sprite.layout.id);
      }
    } else {
      api.patchPosition(sprite.layout.id, sprite.layout.x, sprite.layout.y);
    }
    sprite.container.zIndex = Math.max(1, 4 - sprite.layout.level);
    drag = null;
    requestRender();
  };

  viewport.on('pointerup', endDrag);

  window.addEventListener('pointerup', () => {
    if (!drag) return;
    drag.sprite.container.zIndex = Math.max(1, 4 - drag.sprite.layout.level);
    drag = null;
    requestRender();
  });

  // ─── Smooth scale + alpha tween on a Container ─────────────────────────
  const buttonTweens = new WeakMap<Container, number>();

  const cancelTween = (target: Container): void => {
    const id = buttonTweens.get(target);
    if (id !== undefined) {
      cancelAnimationFrame(id);
      buttonTweens.delete(target);
    }
  };

  const tweenButton = (target: Container, toScale: number, toAlpha: number): void => {
    cancelTween(target);
    const startScale = target.scale.x;
    const startAlpha = target.alpha;
    const startTime  = performance.now();

    const tick = (now: number): void => {
      const t  = Math.min(1, (now - startTime) / STATUS_TWEEN_DURATION_MS);
      const et = t * (2 - t); // easeOutQuad
      target.scale.set(startScale + (toScale - startScale) * et);
      target.alpha = startAlpha + (toAlpha - startAlpha) * et;
      requestRender();
      if (t < 1) {
        buttonTweens.set(target, requestAnimationFrame(tick));
      } else {
        buttonTweens.delete(target);
      }
    };
    buttonTweens.set(target, requestAnimationFrame(tick));
  };

  // ─── Bounce (status badge punch) ───────────────────────────────────────
  const bounceTweens = new WeakMap<Container, number>();
  const cancelBounce = (target: Container): void => {
    const id = bounceTweens.get(target);
    if (id !== undefined) {
      cancelAnimationFrame(id);
      bounceTweens.delete(target);
    }
  };
  const bounce = (target: Container, peak = 1.4, duration = 250): void => {
    cancelBounce(target);
    const start = performance.now();
    const tick = (now: number): void => {
      if (target.destroyed) {
        bounceTweens.delete(target);
        return;
      }
      const elapsed = now - start;
      if (elapsed >= duration) {
        target.scale.set(1);
        bounceTweens.delete(target);
        requestRender();
        return;
      }
      // Bell curve: 1 → peak → 1 via sine.
      const t = elapsed / duration;
      target.scale.set(1 + (peak - 1) * Math.sin(t * Math.PI));
      requestRender();
      bounceTweens.set(target, requestAnimationFrame(tick));
    };
    bounceTweens.set(target, requestAnimationFrame(tick));
  };

  // ─── Shake (negative feedback when a node is blocked) ──────────────────
  const shakeTweens = new WeakMap<Container, number>();
  const cancelShake = (target: Container): void => {
    const id = shakeTweens.get(target);
    if (id !== undefined) {
      cancelAnimationFrame(id);
      shakeTweens.delete(target);
    }
  };
  const shake = (sprite: NodeSprite, peak = 6, duration = 400): void => {
    const c = sprite.container;
    cancelShake(c);
    const start = performance.now();
    const tick = (now: number): void => {
      // Bail out if the user is currently dragging this sprite or it was destroyed.
      if (c.destroyed || drag?.sprite === sprite) {
        shakeTweens.delete(c);
        return;
      }
      const elapsed = now - start;
      if (elapsed >= duration) {
        c.x = sprite.layout.x;
        shakeTweens.delete(c);
        requestRender();
        return;
      }
      const t  = elapsed / duration;
      const dx = peak * Math.sin(t * Math.PI * 5) * (1 - t); // 5 oscillations, damped
      c.x = sprite.layout.x + dx;
      requestRender();
      shakeTweens.set(c, requestAnimationFrame(tick));
    };
    shakeTweens.set(c, requestAnimationFrame(tick));
  };

  // ─── Drop-in (new sprite scales from 0 → 1 with easeOutBack) ───────────
  const dropIn = (target: Container, duration = 360): void => {
    target.scale.set(0);
    const c1 = 1.70158;
    const c3 = c1 + 1;
    const start = performance.now();
    const tick = (now: number): void => {
      if (target.destroyed) return;
      const elapsed = now - start;
      if (elapsed >= duration) {
        target.scale.set(1);
        requestRender();
        return;
      }
      const t = elapsed / duration;
      const u = t - 1;
      const e = 1 + c3 * u * u * u + c1 * u * u;
      target.scale.set(Math.max(0, e));
      requestRender();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ─── Crumple (sprite collapses + fades, then destroys itself) ──────────
  const crumple = (target: Container, duration = 280): void => {
    const start = performance.now();
    const startScale = target.scale.x;
    const startAlpha = target.alpha;
    const tick = (now: number): void => {
      if (target.destroyed) return;
      const elapsed = now - start;
      if (elapsed >= duration) {
        target.destroy({ children: true });
        requestRender();
        return;
      }
      const t = elapsed / duration;
      const e = t * t; // easeInQuad
      target.scale.set(Math.max(0, startScale * (1 - e)));
      target.alpha = Math.max(0, startAlpha * (1 - e));
      requestRender();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ─── Drag trail (fading ghost disc dropped at the dragged node's path) ─
  let lastTrailAt = 0;
  const spawnTrailGhost = (sprite: NodeSprite): void => {
    const ghost = new Graphics();
    ghost.eventMode = 'none';
    const theme = getTheme();
    const color = theme.nodeColors[
      Math.min(sprite.layout.level, theme.nodeColors.length - 1)
    ] as number;
    ghost.circle(0, 0, sprite.layout.radius);
    ghost.fill({ color, alpha: 0.35 });
    ghost.x = sprite.layout.x;
    ghost.y = sprite.layout.y;
    // Insert behind sprites: linesGraphics sits at viewport child index 0,
    // adding here puts the ghost just above lines and below the live nodes.
    viewport.addChildAt(ghost, 1);

    const start = performance.now();
    const duration = 420;
    const tick = (now: number): void => {
      if (ghost.destroyed) return;
      const elapsed = now - start;
      if (elapsed >= duration) {
        ghost.destroy();
        requestRender();
        return;
      }
      ghost.alpha = 0.35 * (1 - elapsed / duration);
      requestRender();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ─── Path pulse: sparkle dot(s) flowing root → selected → every leaf ───
  let pathPulseRafId: number | null = null;
  let pathPulseDots:  Graphics[]    = [];

  const cancelPathPulse = (): void => {
    if (pathPulseRafId !== null) {
      cancelAnimationFrame(pathPulseRafId);
      pathPulseRafId = null;
    }
    for (const dot of pathPulseDots) {
      if (!dot.destroyed) dot.destroy();
    }
    pathPulseDots = [];
  };

  const pathToRoot = (sprite: NodeSprite): NodeLayout[] => {
    const path: NodeLayout[] = [];
    let cursor: NodeLayout | undefined = sprite.layout;
    while (cursor) {
      path.unshift(cursor);
      cursor = cursor.parentId ? layouts.get(cursor.parentId) : undefined;
    }
    return path;
  };

  /** All paths from `start` to every leaf (DFS). For a leaf, returns [[start]]. */
  const leafPathsFrom = (start: NodeLayout): NodeLayout[][] => {
    const out: NodeLayout[][] = [];
    const dfs = (node: NodeLayout, acc: NodeLayout[]): void => {
      const next = [...acc, node];
      if (node.childIds.length === 0) {
        out.push(next);
        return;
      }
      for (const childId of node.childIds) {
        const child = layouts.get(childId);
        if (child) dfs(child, next);
      }
    };
    dfs(start, []);
    return out;
  };

  /** Full root → leaf paths through the selected node. */
  const buildSelectionPaths = (sprite: NodeSprite): NodeLayout[][] => {
    const trunk = pathToRoot(sprite);                        // [root, …, selected]
    const tails = leafPathsFrom(sprite.layout);              // [[selected, …, leaf], …]
    return tails.map(tail => [...trunk.slice(0, -1), ...tail]);
  };

  /** Smooth oval ("comet") — angle-free. Rotation is set per-frame to align with motion. */
  const makeOvalDot = (color: number, halfW = 9, halfH = 4.5): Graphics => {
    const g = new Graphics();
    g.eventMode = 'none';
    g.ellipse(0, 0, halfW, halfH).fill({ color });
    return g;
  };

  const PATH_PULSE_PAUSE_MS    = 400;
  const PATH_PULSE_EDGE_MS     = 650; // slower: 650 ms per edge
  const MAX_PATH_PULSE_LEAVES  = 24;  // cap to keep deep-root selections cheap

  const linePulsePaths = (paths: NodeLayout[][], durationPerEdge = PATH_PULSE_EDGE_MS): void => {
    cancelPathPulse();
    let valid = paths.filter(p => p.length >= 2);
    if (valid.length === 0) return;
    if (valid.length > MAX_PATH_PULSE_LEAVES) {
      // Fisher-Yates partial shuffle, then keep the first N.
      // Random sampling keeps the visual evenly distributed across branches.
      const arr = valid.slice();
      for (let i = 0; i < MAX_PATH_PULSE_LEAVES; i++) {
        const j = i + Math.floor(Math.random() * (arr.length - i));
        const tmp = arr[i] as NodeLayout[];
        arr[i] = arr[j] as NodeLayout[];
        arr[j] = tmp;
      }
      valid = arr.slice(0, MAX_PATH_PULSE_LEAVES);
    }

    const totals: number[] = [];
    const dots:   Graphics[] = [];
    const color  = getTheme().pulseColor;

    for (const path of valid) {
      const dot = makeOvalDot(color);
      const first = path[0] as NodeLayout;
      dot.x = first.x;
      dot.y = first.y;
      dot.alpha = 0;
      viewport.addChild(dot);
      dots.push(dot);
      totals.push(durationPerEdge * (path.length - 1));
    }
    pathPulseDots = dots;

    const maxTotal   = Math.max(...totals);
    const cycle      = maxTotal + PATH_PULSE_PAUSE_MS;
    let   cycleStart = performance.now();

    const tick = (now: number): void => {
      const elapsed = now - cycleStart;
      if (elapsed >= cycle) {
        cycleStart = now;
        pathPulseRafId = requestAnimationFrame(tick);
        return;
      }

      for (let i = 0; i < valid.length; i++) {
        const path  = valid[i];
        const dot   = dots[i];
        const total = totals[i];
        if (!path || !dot || total === undefined || dot.destroyed) continue;

        if (elapsed >= total) {
          if (dot.alpha !== 0) dot.alpha = 0;
          continue;
        }

        const globalT      = elapsed / total;
        const edgeProgress = globalT * (path.length - 1);
        const edgeIdx      = Math.max(0, Math.min(Math.floor(edgeProgress), path.length - 2));
        const localT       = edgeProgress - edgeIdx;
        const from         = path[edgeIdx];
        const to           = path[edgeIdx + 1];
        // Defensive: the path is captured by closure, so a node deleted between
        // selection and this tick still has its NodeLayout in JS memory — but
        // log + skip if for some reason an endpoint is missing.
        if (!from || !to) continue;

        const e = localT < 0.5
          ? 4 * localT * localT * localT
          : 1 - Math.pow(-2 * localT + 2, 3) / 2;
        dot.x = from.x + (to.x - from.x) * e;
        dot.y = from.y + (to.y - from.y) * e;
        dot.rotation = Math.atan2(to.y - from.y, to.x - from.x);
        dot.alpha = globalT < 0.1 ? globalT / 0.1
                 : globalT > 0.9 ? (1 - globalT) / 0.1
                 : 1;
      }
      requestRender();
      pathPulseRafId = requestAnimationFrame(tick);
    };
    pathPulseRafId = requestAnimationFrame(tick);
  };

  // ─── Line pulse (dot travelling along a freshly-created edge) ──────────
  const linePulse = (parent: NodeLayout, child: NodeLayout, duration = 600): void => {
    const dot = new Graphics();
    dot.eventMode = 'none';
    dot.circle(0, 0, 5);
    dot.fill({ color: getTheme().pulseColor });
    dot.x = parent.x;
    dot.y = parent.y;
    viewport.addChild(dot);

    const start = performance.now();
    const tick = (now: number): void => {
      const elapsed = now - start;
      if (elapsed >= duration) {
        dot.destroy();
        requestRender();
        return;
      }
      const t = elapsed / duration;
      // easeInOutCubic
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      dot.x = parent.x + (child.x - parent.x) * e;
      dot.y = parent.y + (child.y - parent.y) * e;
      // Trapezoid alpha: fade in over first 15 %, hold, fade out over last 15 %.
      const alpha = t < 0.15 ? t / 0.15
                  : t > 0.85 ? (1 - t) / 0.15
                  : 1;
      dot.alpha = alpha;
      requestRender();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const pulseRing = (target: Container, baseRadius: number, repeats: number, color?: number): void => {
    const ring = new Graphics();
    target.addChild(ring);

    const start = performance.now();
    const total = PULSE_DURATION_MS * repeats;

    const tick = (now: number): void => {
      const elapsed = now - start;
      if (elapsed >= total) {
        ring.destroy();
        requestRender();
        return;
      }
      const local = (elapsed % PULSE_DURATION_MS) / PULSE_DURATION_MS;
      const r     = baseRadius + (baseRadius * 1.6) * local;
      const alpha = 0.85 * (1 - local);
      ring.clear();
      ring.circle(0, 0, r);
      ring.stroke({ width: 3, color: color ?? getTheme().pulseColor, alpha });
      requestRender();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const pulseNode = (id: string): void => {
    const sprite = sprites.get(id);
    if (!sprite) return;
    pulseRing(sprite.container, sprite.layout.radius, PULSE_REPEATS);
  };

  // ─── Firework / success animation ──────────────────────────────────────
  const FIREWORK_PARTICLE_COUNT = 16;
  const FIREWORK_DURATION_MS    = 1100;
  const FIREWORK_MIN_DISTANCE   = 90;
  const FIREWORK_MAX_DISTANCE   = 220;
  const FIREWORK_PARTICLE_R     = 13;
  const FIREWORK_ICON_SIZE      = 14;
  // Bright, mutually-distinct fills.
  const FIREWORK_COLORS: number[] = [
    0xff6b9d, 0xfeca57, 0x48dbfb, 0x1dd1a1,
    0xff9ff3, 0xff7f50, 0xa29bfe, 0xffd93d,
  ];

  const triggerFirework = (sprite: NodeSprite): void => {
    interface Particle {
      container: Container;
      angle:     number;
      distance:  number;
      spin:      number;
    }
    const particles: Particle[] = [];

    for (let i = 0; i < FIREWORK_PARTICLE_COUNT; i++) {
      // Even radial distribution + small jitter so it doesn't look mechanical.
      const angle    = (i / FIREWORK_PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const distance = FIREWORK_MIN_DISTANCE + Math.random() * (FIREWORK_MAX_DISTANCE - FIREWORK_MIN_DISTANCE);
      const color    = FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)] as number;
      const iconName = FIREWORK_ICONS[Math.floor(Math.random() * FIREWORK_ICONS.length)] as keyof typeof ICONS;
      const spin     = (Math.random() - 0.5) * Math.PI * 1.2;

      const p = new Container();
      p.eventMode = 'none';

      const bg = new Graphics();
      bg.circle(0, 0, FIREWORK_PARTICLE_R);
      bg.fill({ color });
      p.addChild(bg);

      const iconGfx = new Graphics();
      drawIcon(iconGfx, ICONS[iconName], 0xffffff, FIREWORK_ICON_SIZE, 2.4);
      p.addChild(iconGfx);

      sprite.container.addChild(p);
      particles.push({ container: p, angle, distance, spin });
    }

    // Boost zIndex during the burst so particles fly above neighbouring nodes.
    const prevZ = sprite.container.zIndex;
    sprite.container.zIndex = 1000;

    const start = performance.now();
    const tick = (now: number): void => {
      const elapsed = now - start;
      if (elapsed >= FIREWORK_DURATION_MS) {
        for (const p of particles) p.container.destroy({ children: true });
        sprite.container.zIndex = prevZ;
        requestRender();
        return;
      }
      const t    = elapsed / FIREWORK_DURATION_MS;
      const ease = 1 - Math.pow(1 - t, 3);             // easeOutCubic for distance
      const fade = t < 0.65 ? 1 : 1 - (t - 0.65) / 0.35; // hold then fade

      for (const p of particles) {
        const r = p.distance * ease;
        p.container.x = Math.cos(p.angle) * r;
        // Slight gravity pull so particles drop a bit at the end.
        p.container.y = Math.sin(p.angle) * r + 40 * t * t;
        p.container.alpha = Math.max(0, fade);
        p.container.rotation = p.spin * t;
        p.container.scale.set(0.5 + 0.5 * (1 - t));
      }
      requestRender();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // Space → mark selected node as done + firework.
  window.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.code !== 'Space' || e.repeat || !selected) return;
    const target = e.target as HTMLElement | null;
    const tag    = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
    e.preventDefault();

    if (selected.layout.status !== 'done') {
      selected.statusBar.buttons.forEach(b => cancelTween(b.container));
      selected.layout.status = 'done';
      refreshNodeStatus(selected, getTheme());
      bounce(selected.statusBadge.container);
      api.patchStatus(selected.layout.id, 'done');
      onLayoutsChanged?.();
    }
    triggerFirework(selected);
  });

  const selectNode = (id: string): void => {
    const sprite = sprites.get(id);
    if (sprite) select(sprite);
  };

  // Mirrors a user tapping a node: zoom in to at least FOCUS_ZOOM (without
  // de-zooming if already deeper), pulse, and select. Same path as the node-list
  // panel click and the canvas focus.
  // Event pulse — zoom/pan to the node, THEN (once the zoom lands) play the
  // high-contrast ring and run onArrive. No selection, no panel navigation, so
  // the view scope is preserved. Returns false if the node isn't on this page.
  const pulseEvent = (id: string, onArrive?: () => void): boolean => {
    const sprite = sprites.get(id);
    if (!sprite) return false;
    pan.focusTo(sprite.layout.x, sprite.layout.y, Math.max(viewport.scale.x, FOCUS_ZOOM), () => {
      const s = sprites.get(id); // node may have been removed during the pan
      if (!s) return;
      pulseRing(s.container, s.layout.radius, PULSE_REPEATS, EVENT_PULSE_COLOR);
      requestRender();
      onArrive?.();
    });
    return true;
  };

  // Card click: zoom to the node, then play its event animation (pulse + status
  // flourish) on arrival. Does not change the node's state or the panel scope.
  const playEventAnimation = (id: string, status: Status | null): boolean =>
    pulseEvent(id, () => {
      const s = sprites.get(id);
      if (!s) return;
      if (status === 'done')          triggerFirework(s);
      else if (status === 'blocked') { shake(s); bounce(s.statusBadge.container); }
      else if (status)                bounce(s.statusBadge.container);
      requestRender();
    });

  const getSelectedId = (): string | null => selected?.layout.id ?? null;

  // Reflect a status the API already persisted (via an event) onto the sprite.
  // Mirrors the status-bar click path, minus the api.patchStatus write.
  const applyStatusVisual = (id: string, status: Status): void => {
    const sprite = sprites.get(id);
    if (!sprite || sprite.layout.status === status) return;
    sprite.statusBar.buttons.forEach(b => cancelTween(b.container));
    sprite.layout.status = status;
    refreshNodeStatus(sprite, getTheme());
    bounce(sprite.statusBadge.container);
    if (status === 'blocked') shake(sprite);
    if (status === 'done') triggerFirework(sprite);
    requestRender();
    onLayoutsChanged?.();
  };

  // Reflect a label the API already persisted (via an event) onto the sprite.
  const applyLabelVisual = (id: string, label: string): void => {
    const sprite = sprites.get(id);
    if (!sprite || sprite.layout.label === label) return;
    sprite.layout.label = label;
    sprite.text.text    = label;
    requestRender();
    onLayoutsChanged?.();
  };

  return {
    registerNode,
    focusOn: pan.focusTo,
    pulseNode,
    selectNode,
    pulseEvent,
    playEventAnimation,
    getSelectedId,
    applyStatusVisual,
    applyLabelVisual,
    redrawLines,
  };
}
